import { useCallback, useEffect, useState } from 'react';
import { notifications as notificationsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Can, Pagination, dateTime } from '../components/Common';

export default function Notifications() {
  const toast = useToast();

  const [data, setData] = useState({ notifications: [], meta: null });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ title: '', body: '', audience: 'all-customers', type: 'general', channels: ['push'] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await notificationsApi.list({ page, limit: 20 });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load notifications', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Notifications · UrbanClean'; }, []);

  const toggleChannel = (channel) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(channel)
        ? f.channels.filter((c) => c !== channel)
        : [...f.channels, channel],
    }));
  };

  const send = async () => {
    if (!form.title.trim()) return toast.error('Give the notification a title');
    setBusy(true);
    try {
      const res = await notificationsApi.broadcast(form);
      toast.success(res.message);
      setForm((f) => ({ ...f, title: '', body: '' }));
      load();
    } catch (err) {
      toast.error('Could not send', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Notifications</h1>
          <div className="sub">Broadcast to customers over push, SMS and WhatsApp</div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(300px, 1fr) minmax(0, 1.6fr)' }}>
        <Can permission="notification.create" fallback={null}>
          <div className="card" style={{ alignSelf: 'start' }}>
            <div className="card-head"><h3>Send a broadcast</h3></div>
            <div className="card-body">
              <div className="field">
                <label>Title *</label>
                <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Monsoon offer is live" />
              </div>
              <div className="field">
                <label>Message</label>
                <textarea className="textarea" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="Flat 30% off on blanket cleaning this week." />
              </div>
              <div className="field">
                <label>Audience</label>
                <select className="select" value={form.audience} onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}>
                  <option value="all-customers">All customers</option>
                  <option value="broadcast">Public announcement (no per-user copy)</option>
                </select>
              </div>
              <div className="field">
                <label>Channels</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['push', 'sms', 'email', 'whatsapp'].map((c) => (
                    <label key={c} className={`perm-chip ${form.channels.includes(c) ? 'on' : ''}`}>
                      <input type="checkbox" checked={form.channels.includes(c)} onChange={() => toggleChannel(c)} />
                      {c}
                    </label>
                  ))}
                </div>
                <div className="hint">SMS and WhatsApp go out once those gateway credentials are configured.</div>
              </div>
              <button className="btn btn-primary btn-block" onClick={send} disabled={busy}>
                {busy ? 'Sending…' : 'Send notification'}
              </button>
            </div>
          </div>
        </Can>

        <div className="card">
          <div className="card-head"><h3>Sent history</h3></div>
          {loading ? <Loading /> : data.notifications.length === 0 ? (
            <Empty title="Nothing sent yet" />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Notification</th><th>Type</th><th>Channels</th><th>Sent</th></tr></thead>
                  <tbody>
                    {data.notifications.map((n) => (
                      <tr key={n._id}>
                        <td>
                          <div className="strong">{n.title}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{n.body}</div>
                        </td>
                        <td><span className="badge badge-gray">{n.type}</span></td>
                        <td className="muted" style={{ fontSize: 12 }}>{(n.channels || []).join(', ')}</td>
                        <td className="muted nowrap">{dateTime(n.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination meta={data.meta} onPage={setPage} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
